'use client';

import { ReadonlyURLSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import { ProductsItem } from '@app-types/api/statics';
import { EligibilityConfigType } from '@app-types/config';
import { IRudiApiRechercherAdresse, LmPartner, ShortListContext } from '@app-types/eligibility';
import { UpsellModalComponentType } from '@app-types/eligibility/config';
import { FaiType } from '@app-types/plan';
import { UserType } from '@app-types/user';

import { UpsellModalContainerProps } from '@components/fai-upsell-modal';

import { formatTagText } from '@helpers/tracking';

import { TYPE_ELIGIBILITY } from '@constants/eligibility';
import { PLAY, TECHNOLOGY } from '@constants/provider';

import { EligibilityAddress } from './eligibility-address';
import { EligibilityOffer } from './eligibility-offer';
import { EligibilityResults } from './eligibility-results';
import { setThreeClicsStorage } from './eligibility-storage';
import { EligibilityResultType } from './eligibility-widget';
import { JourneyConfigType } from './journey-config';
import { getShortListName, splitShortListParams } from './short-list';
import { normalizeTechnology } from './technology';
import { sendTrackingAddFaiToCart, sendUpsellModalClick, sendUpsellModalClose, sendUpsellModalOpen } from './tracking';
import { useEligibilitySteps } from './use-eligibility-steps';

/**
 * Hook to manage the eligibility state.
 * Should be used in one file (does not use context).
 */

export function useEligibility(
    eligibilityConfig: EligibilityConfigType,
    journeyConfig: JourneyConfigType,
    addToCart: (
        gencodes: string[],
        idPanierCorbis: string,
        redirect?: boolean,
        callback?: () => void,
        homeZone?: string,
    ) => Promise<void>,
    searchParams: ReadonlyURLSearchParams,
    staticsData: ProductsItem[],
    user?: UserType,
    lmPartnerData?: LmPartner,
) {
    const [isMapFirstStep, setIsMapFirstStep] = useState<boolean>(false);
    const [address, setAddress] = useState<EligibilityAddress>();
    const [results, setResults] = useState<EligibilityResults>();
    const [upsellModal, setUpsellModal] = useState<UpsellModalContainerProps | null>(null);

    const onResetExtras = () => {
        EligibilityResults.clear();
        setResults(undefined);
        setIsMapFirstStep(false);
        setAddress(undefined);
    };

    const {
        error,
        setError,
        currentStep,
        setCurrentStep,
        currentStepStatus,
        setCurrentStepStatus,
        isFirstStep,
        setIsFirstStep,
        resetSteps,
        handleStepStatusChange,
        handleStepChange,
        handleEligibilityChange,
    } = useEligibilitySteps({ onReset: onResetExtras });

    const onResetAddress = () => {
        resetSteps();
        onResetExtras();
        setThreeClicsStorage(false);
    };

    const shortList: ShortListContext | undefined = useMemo(() => {
        const gamme = searchParams.get('gamme');
        const focus = searchParams.get('focus');

        if (gamme || focus) {
            const { rangeNgCommercialSeries, technology } = splitShortListParams(gamme ?? focus ?? undefined);
            const name = getShortListName(
                rangeNgCommercialSeries,
                technology?.toUpperCase() as TECHNOLOGY,
                staticsData,
            );

            if (technology && name) {
                return {
                    name,
                    technology,
                    focus: !!focus,
                    rangeNgCommercialSeries,
                };
            }
        }
    }, [searchParams]);

    const _setAddress = (value: IRudiApiRechercherAdresse) => {
        const newAddress = new EligibilityAddress(value);
        setAddress(newAddress);
    };

    const openOfferUpsellModal = (
        clickedOffer: EligibilityOffer,
        upsellOffer: EligibilityOffer,
        eligibilityResults: EligibilityResults,
        modal?: UpsellModalComponentType,
        isThreeClicsFtth: boolean = false,
    ): void => {
        sendUpsellModalOpen(journeyConfig.tracking);

        const onClose = () => {
            sendUpsellModalClose(journeyConfig.tracking);
            setUpsellModal(null);
        };

        const onAddOffer = async (_offer: FaiType | EligibilityOffer) => {
            const offer = _offer as EligibilityOffer;

            sendUpsellModalClick(journeyConfig.tracking, offer.rangeNg + offer.commercialSeries);
            const autoAdd = offer?.neededElements?.map((el) => el.gencode) ?? [];
            const homeZone =
                offer.technology === TECHNOLOGY.BOX_5G
                    ? eligibilityResults.results.box5g.homeZone
                    : eligibilityResults.results.box4g.homeZone;

            if (isThreeClicsFtth) {
                setThreeClicsStorage(true);
            }
            void addToCart(
                [offer.sku, ...autoAdd],
                eligibilityResults.idPanierCorbis,
                true,
                () => {
                    setUpsellModal(null);
                    const play = offer.play ? ` ${offer.play}` : '';
                    const offerName = `${offer?.name}${play}`;
                    const trackingAction = `Ajouter_au_panier_${formatTagText(offerName)}`;
                    sendTrackingAddFaiToCart(journeyConfig, trackingAction, offer?.technology === TECHNOLOGY.FTTH);
                },
                offer.rangeNg === 'xgbox' ? homeZone : undefined,
            );
        };

        if (modal) {
            setUpsellModal({ type: modal, clickedOffer, upsellOffer, onClose, onAddOffer });
        }
    };

    const handleSkipToCart = async (_results: EligibilityResults, shortList: ShortListContext): Promise<boolean> => {
        // Retrieve offers for the technology specified in shortList
        const offers = _results.offers[normalizeTechnology(shortList.technology)];

        if (offers) {
            // Get the targeted offer by prioritizing 3P offers
            const offer = offers
                .filter(
                    (offer) =>
                        offer.rangeNg + '_' + offer.commercialSeries === shortList?.rangeNgCommercialSeries ||
                        offer.range === shortList?.rangeNgCommercialSeries,
                )
                .sort((offer1, offer2) => {
                    const isFirstOffer3P = offer1.play === PLAY._3P;
                    const isSecondOffer3P = offer2.play === PLAY._3P;

                    if (isFirstOffer3P && !isSecondOffer3P) {
                        return -1; // offer1 comes first
                    } else if (!isFirstOffer3P && isSecondOffer3P) {
                        return 1; // offer2 comes first
                    }
                    return 0; // No change in order
                })[0];

            if (offer) {
                const isFastPass = Boolean(
                    searchParams.get('zipCode') && searchParams.get('inseeCode') && searchParams.get('rivoliCode'),
                );
                const isThreeClicsFtth = isFastPass && offer.technology === TECHNOLOGY.FTTH;

                // Check if the offer is upsellable
                const upsellData = journeyConfig.productsToUpsell.find(({ baseProduct }) => {
                    return offer.isSameRangeAndPlay(
                        baseProduct.rangeNg,
                        baseProduct.commercialSeries,
                        baseProduct.play,
                    );
                });

                if (upsellData) {
                    const upsellOffer = offers.find(
                        (item) =>
                            upsellData.upselledProduct &&
                            item.isSameRangeAndPlay(
                                upsellData.upselledProduct?.rangeNg,
                                upsellData.upselledProduct?.commercialSeries,
                                upsellData.upselledProduct?.play,
                            ),
                    );

                    if (upsellOffer && upsellData.modalComponentType) {
                        openOfferUpsellModal(
                            offer,
                            upsellOffer,
                            _results,
                            upsellData.modalComponentType,
                            isThreeClicsFtth,
                        );
                        setResults(_results);
                        return false;
                    }
                }

                // If no upsell, add the offer to the cart
                const autoAdd = offer?.neededElements?.map((el) => el.gencode) ?? [];
                const homeZone =
                    offer.technology === TECHNOLOGY.BOX_5G
                        ? _results.results.box5g.homeZone
                        : _results.results.box4g.homeZone;
                setThreeClicsStorage(isThreeClicsFtth);
                await addToCart(
                    [offer.sku, ...autoAdd],
                    _results.idPanierCorbis,
                    true,
                    undefined,
                    offer.rangeNg === 'xgbox' ? homeZone : undefined,
                );
                return true;
            }
        }
        return false;
    };

    const _setResults = async (type: TYPE_ELIGIBILITY, result: EligibilityResultType) => {
        new EligibilityResults(
            type,
            result,
            {
                eligibilityConfig,
                journeyConfig,
                lmPartnerData,
            },
            user,
            async (newResults) => {
                // If "direct panier"
                if (newResults && shortList?.focus === false && (await handleSkipToCart(newResults, shortList))) {
                    return;
                }
                setResults(newResults);
            },
        );
    };

    useEffect(() => {
        async function getResultFromStorage() {
            EligibilityResults.fromLocalStorage(
                {
                    eligibilityConfig,
                    journeyConfig,
                    lmPartnerData,
                },
                user,
                async (newResults) => {
                    if (newResults && shortList?.focus === false) {
                        const shouldDisplayOffers = !(await handleSkipToCart(newResults, shortList));
                        if (!shouldDisplayOffers) {
                            return;
                        }
                    }

                    setResults(newResults);
                },
            );
        }

        getResultFromStorage().catch(() => {
            EligibilityResults.clear();
        });
    }, []);

    return {
        error,
        setError,
        currentStep,
        setCurrentStep,
        currentStepStatus,
        setCurrentStepStatus,
        isFirstStep,
        setIsFirstStep,
        isMapFirstStep,
        setIsMapFirstStep,
        address,
        setAddress: _setAddress,
        results,
        setResults: _setResults,
        onResetAddress,
        upsellModal,
        openOfferUpsellModal,
        shortList,
        handleStepStatusChange,
        handleEligibilityChange,
        handleStepChange,
    };
}
